/**
 * The words a trainer meets on the group page and may not know, said in two
 * lines, and the three steps of a course (06.10.2026). One place, so the "?"
 * next to a term and the guide at first visit never tell two stories.
 */
export const GLOSSARY = {
  todo: {
    title: "Ce ai de făcut",
    text: "Lista se face singură, din ce s-a întâmplat la grupă: o sesiune fără prezență, o temă fără feedback, "
      + "un portofoliu de verificat. Un rând dispare când l-ai rezolvat. Cele întârziate apar primele.",
  },
  asyncHours: {
    title: "Ore asincrone",
    text: "Cele 10 ore pe care cursanții le lucrează singuri, din cele 25 ale programului. Se socotesc din temele "
      + "predate și materialele deschise care au bifat „Intră în cele 10 ore asincrone”.",
  },
  liveAttendance: {
    title: "Prezența live",
    text: "Cele 15 ore pe Meet, socotite separat la teorie și la practică. Peste 20% absențe pe o componentă, "
      + "cursantul e sub prag: recuperează (dacă a lipsit cel mult 50%) sau e eliminat.",
  },
  threshold: {
    title: "Pragul",
    text: "Cel mult 20% absențe pe fiecare componentă (teorie, practică), la final. Până la ultima sesiune, "
      + "cifrele arată cât s-a făcut, nu un verdict.",
  },
  annexes: {
    title: "Anexele",
    text: "Documentele cerute de procedura ministerului pentru dosarul cursului. Portalul le completează din ce "
      + "marchezi tu; un document generat nu se mai schimbă, o corectură e o versiune nouă.",
  },
  structure: {
    title: "Verificarea structurii",
    text: "După ultima sesiune: pentru fiecare portofoliu depus, DA sau NU la 7 elemente (activități, resursă, "
      + "diseminare, reflecție, semnătură). Nu e notare: punctajul îl dă comisia.",
  },
};

/** The guide at first visit: the course in three steps, by stage. */
export const STEPS = [
  {
    key: "prepare", stages: ["before"], title: "Înainte de curs",
    text: "Publici temele și materialele din pagina principală a portalului. La cele care fac parte din ore, "
      + "bifezi „Intră în cele 10 ore asincrone”, până acoperi toate cele 10.",
  },
  {
    key: "teach", stages: ["course"], title: "În timpul cursului",
    text: "După fiecare sesiune marchezi prezența și atașezi raportul din Meet. La teme dai feedback în cel "
      + "mult 3 zile de la predare.",
  },
  {
    key: "check", stages: ["portfolio", "evaluation", "done"], title: "După ultima sesiune",
    text: "Cursanții își depun portofoliul în 5 zile; tu îi verifici structura. Dacă ești în comisie, punctezi "
      + "portofoliile și apoi susținerea.",
  },
];

/** The same, for a Curs FPED cohort (12.10.2026): no hours, attendance, portfolio or committee. */
export const FPED_STEPS = [
  {
    key: "prepare", stages: ["before"], title: "Înainte de curs",
    text: "Publici temele și materialele din pagina principală a portalului.",
  },
  {
    key: "teach", stages: ["course"], title: "În timpul cursului",
    text: "După fiecare sesiune marchezi prezența, în fila „Prezență”. Cursanții își predau temele în portal, "
      + "iar tu dai feedback în cel mult 3 zile de la predare.",
  },
  {
    key: "check", stages: ["done"], title: "După ultima sesiune",
    text: "Dai feedback la temele rămase. Echipa FPED stabilește cine a absolvit și emite certificatele.",
  },
];

export function stepOf(stage, steps = STEPS) {
  return steps.find((s) => s.stages.includes(stage))?.key || steps[0].key;
}

const SEEN_KEY = "portal.groupIntro.seenGroups";
const SEEN_MAX = 50;

function seenGroups() {
  try {
    const list = JSON.parse(window.localStorage.getItem(SEEN_KEY) || "[]");
    return Array.isArray(list) ? list : [];
  } catch {
    return [];
  }
}

/** Whether this browser has closed the guide for this group (19.10.2026: per
 *  group — a new group starts with it open). Storage may be off: then the
 *  guide simply shows again, it never breaks the page. */
export function introSeen(groupId) {
  return seenGroups().includes(groupId);
}

export function markIntroSeen(groupId) {
  try {
    const list = [groupId, ...seenGroups().filter((g) => g !== groupId)].slice(0, SEEN_MAX);
    window.localStorage.setItem(SEEN_KEY, JSON.stringify(list));
  } catch {
    /* storage off — nothing to remember */
  }
}

/** "8 cursanți în portal" — or, while the trainer prepares the group, that they cannot enter yet. */
export function participantsLine(count, invited = true) {
  if (!count) return invited ? "Niciun cursant în portal încă" : "Niciun cursant cu contract semnat încă";
  const people = count === 1 ? "Un cursant" : `${count} cursanți`;
  if (invited) return `${people} în portal`;
  return `${people} cu contract semnat, încă neinvitați în portal`;
}
